import React from 'react';
import { compose } from 'redux';
import {
  PapiGallection,
  PapiDeviation,
  PapiPremiumFolderData,
} from '@wix/da-types-papi';
import ConditionalWrapper from '@wix/da-shared-react/pkg/ConditionalWrapper';
import OverlayContext from '@wix/da-shared-react/pkg/DeviationViews/Thumb/Standard/Overlay/OverlayContext';
import LoadingIndicator from '@wix/da-shared-react/pkg/LoadingIndicator';
import history from '@wix/da-shared-react/pkg/redux/routing/history';
import { scrollIntoViewWithHeader } from '@wix/da-shared-react/pkg/utils/scrollIntoViewWithHeader';
import { SCROLL_TO_FOLDER_HASH_SIGNAL } from '@wix/da-shared-react/pkg/CollectionPanel/CollectNotification';
import OnScrollBottom, {
  DISTANCE_UNIT,
} from '@wix/da-shared-react/pkg/OnScrollBottom';
import FolderDescription from './FolderDescription';
import { GallectionType } from '../../../../../types/gallection';
import { getGallectionsStickyBarHeight } from '../helpers';
import { getStickyContentHeight } from '../../helpers';
import ErrorBoundary from '@wix/da-shared-react/pkg/ErrorBoundary';
import { withMobileContext } from '@wix/da-shared-react/pkg/Context/MobileContext';
import GallectionDeviationsTorpedo from '../GallectionDeviationsTorpedo';
import FolderPaywall from '../../../UserWidgets/_partials/paywalls/FolderPaywall';
import { gallectionTypeToResourceType } from '@wix/da-shared-react/pkg/types/gallection';
import { BiEventType } from '@wix/da-shared-react/pkg/biLogger/constants';
import { normalizePremiumFolderData } from '@wix/da-shared-react/pkg/utils/normalizePremiumFolderData';
import withFeatureFlags, {
  WithFeatureFlagsProps,
} from '@wix/da-shared-react/pkg/redux/flags/withFeatureFlags';
import FolderCommentThread from './FolderCommentThread';
import FolderBreadcrumbs from './FolderBreadcrumbs/';
import { Url } from '@wix/da-url';

import s from './FolderContent.scss';

const FOLDER_BAR_DISTANCE_FROM_TOP = 400;

export interface Props {
  streamId: string;
  folderId: number;
  subfolderId?: number;
  folder: PapiGallection;
  subfolder?: PapiGallection;
  gallectionType: GallectionType;
  deviations: PapiDeviation[];
  isInitialized: boolean;
  hasMore: boolean;
  isSelected: boolean;
  isFetching: boolean;
  isFetchingPaused: boolean;
  initFetch: () => void;
  fetchMore: () => void;
  setFetchingPausedFlag: (value: boolean) => void;
  isOwner: boolean;
  className?: string;
  isMobile?: boolean;
  scrollIntoViewWhenLoaded?: boolean;
  justExitedDuperbrowse?: boolean;
}

class FolderContent extends React.Component<Props & WithFeatureFlagsProps> {
  static displayName = 'FolderContent';

  folderRef: React.RefObject<HTMLDivElement> = React.createRef();
  torpedoRef: React.RefObject<HTMLDivElement> = React.createRef();

  UNSAFE_componentWillMount() {
    this.initFetchIfNeeded(this.props);
  }

  UNSAFE_componentWillReceiveProps(nextProps) {
    this.initFetchIfNeeded(nextProps);
  }

  componentDidMount() {
    const {
      isSelected,
      scrollIntoViewWhenLoaded = false,
      justExitedDuperbrowse = false,
    } = this.props;
    const locationHash = window.location.hash.replace('#', '');
    const scrollForHash =
      isSelected && locationHash === SCROLL_TO_FOLDER_HASH_SIGNAL;
    const scrollForLoad = scrollIntoViewWhenLoaded && !justExitedDuperbrowse;
    if (scrollForHash || scrollForLoad) {
      const withoutHash = { ...window.location, hash: undefined };
      const historyState = history.location.state;
      history.replace(withoutHash as any, historyState);

      this.scrollToThisFolder(scrollForHash);
    }
  }

  initFetchIfNeeded = props => {
    const { deviations, isInitialized, hasMore, isFetching, initFetch } = props;

    if (!deviations.length && (hasMore || !isInitialized) && !isFetching) {
      initFetch();
    }
  };

  render() {
    const {
      subfolder,
      deviations,
      isFetching,
      folder,
      className,
      gallectionType,
      streamId,
      isOwner,
    } = this.props;

    const { pointPrice } = normalizePremiumFolderData(folder.premiumFolderData);
    const isCollection = gallectionType === GallectionType.COLLECTION;
    return (
      <OverlayContext.Provider
        value={{
          showAuthorTooltip: isCollection,
          showPermanentIconWhenFaved: !(isCollection && isOwner),
        }}
      >
        <div className={className} ref={this.folderRef}>
          <ErrorBoundary>
            <FolderDescription
              folder={folder}
              className={s['folder-description']}
            />
            {subfolder && <FolderBreadcrumbs />}
            <OnScrollBottom
              distanceToBottom={2}
              distanceUnit={DISTANCE_UNIT.WINDOW_HEIGHT}
              onScrolledToBottom={this.onScrollBottom}
            >
              <div>
                {deviations.length > 0 && (
                  <ConditionalWrapper
                    condition={!isCollection}
                    wrap={child => (
                      <FolderPaywall
                        premiumFolderData={{
                          ...(folder.premiumFolderData as PapiPremiumFolderData),
                          galleryUrl: Url.userGalleryPath({
                            username: folder.owner.username,
                            gallectionType: 'gallery',
                            folderId: folder.folderId,
                            folderName: folder.name,
                          }),
                        }}
                        contentOwner={folder.owner}
                        folderId={folder.folderId}
                        biData={{
                          evid: BiEventType.GALLERY_PURCHASE,
                          points: pointPrice,
                          typeid: gallectionTypeToResourceType(gallectionType),
                          itemid: folder.folderId,
                          galleryid: folder.folderId,
                          authorid: folder.owner.useridUuid,
                        }}
                      >
                        {child}
                      </FolderPaywall>
                    )}
                  >
                    <div ref={this.torpedoRef}>
                      <GallectionDeviationsTorpedo
                        streamId={streamId}
                        gallectionType={gallectionType}
                        gallectionOwner={folder?.owner}
                        deviations={deviations}
                      />
                    </div>
                  </ConditionalWrapper>
                )}
              </div>
            </OnScrollBottom>
            {isFetching && <LoadingIndicator />}
            <FolderCommentThread folder={subfolder || folder} />
          </ErrorBoundary>
        </div>
      </OverlayContext.Provider>
    );
  }

  onScrollBottom = scrollDelta => {
    const {
      hasMore,
      isFetching,
      isFetchingPaused,
      setFetchingPausedFlag,
      fetchMore,
    } = this.props;
    if (isFetching) {
      return;
    }
    if (!hasMore) {
      return;
    }
    // if we scrolled pasted torpedo to the comments block, no need to load more
    if (this.props.deviations.length > 0 && !this.isTorpedoVisible()) {
      if (!isFetchingPaused) {
        setFetchingPausedFlag(true);
      }
      return;
    }
    if (isFetchingPaused) {
      const scrolledUp = scrollDelta < 0;
      if (scrolledUp) {
        setFetchingPausedFlag(false);
      }
      return;
    }
    fetchMore();
  };

  isTorpedoVisible = () => {
    const { isMobile } = this.props;
    const domNode = this.torpedoRef.current;
    if (!domNode) {
      return false;
    }
    const offsetTop =
      getStickyContentHeight(isMobile) +
      getGallectionsStickyBarHeight(isMobile);
    const { bottom } = domNode.getBoundingClientRect();
    return bottom - offsetTop > 0;
  };

  scrollToThisFolder(isInitialLoad = true) {
    const thisFolder = this.folderRef.current;
    const { isMobile } = this.props;
    if (!thisFolder) {
      return;
    }

    const folderBarHeightAndPadding =
      getGallectionsStickyBarHeight(isMobile) + 15;
    scrollIntoViewWithHeader(
      thisFolder,
      isMobile,
      // on the initial page load for some reason, we need to move
      // 400px further down in order to get the top of the folder
      // To add this with scrollIntoViewWithHeader, we need to subtract it
      // from the header height that will be subtracted from the folder's
      // bounds.top
      isInitialLoad
        ? folderBarHeightAndPadding - FOLDER_BAR_DISTANCE_FROM_TOP
        : folderBarHeightAndPadding
    );
  }
}

const enhance = compose(withMobileContext, withFeatureFlags);

export default enhance(FolderContent);
